import { Host as ApiHost } from "../api/hosts";
import { PartnerHost } from "../api/partners";

export interface Host {
  id: string;
  displayName: string;
  email: string;
  createdAtUtc: string;
  updatedAt: string;
  accountState: string;
  /** Set on a partner's staff host (ENG-1958): the partner it hosts for. */
  partnerName?: string;
}

export const fromApiHost = (apiHost: ApiHost): Host => {
  return {
    id: apiHost.fsId,
    displayName: apiHost.displayName,
    email: apiHost.email,
    createdAtUtc: apiHost.createdAtUtc,
    updatedAt: apiHost.updatedAt,
    accountState: apiHost.accountState,
  };
};

/** `accountState` of a placeholder for a host id the admin app cannot name. */
export const UNKNOWN_HOST_STATE = "unknown";

/**
 * A stand-in for a host id that is in neither the Hosts group nor the
 * partner's staff (someone who left the group, a stale id, a staff member
 * of another partner). It keeps the id so a re-save sends it back unchanged
 * instead of silently dropping the host.
 */
export const unknownHost = (id: string): Host => ({
  id,
  displayName: `Unknown host (${id})`,
  email: "",
  createdAtUtc: "",
  updatedAt: "",
  accountState: UNKNOWN_HOST_STATE,
});

export const isUnknownHost = (host: Host): boolean =>
  host.accountState === UNKNOWN_HOST_STATE;

/** The known host for an id, else a placeholder that still carries the id. */
export const resolveHost = (
  id: string,
  hostsById: Record<string, Host>,
): Host => hostsById[id] ?? unknownHost(id);

/** `accountState` of a partner staff host, which has no Hosts-group record. */
export const PARTNER_STAFF_STATE = "partner-staff";

/** A partner's staff member as a host, carrying the partner's name. */
export const fromPartnerHost = (
  partnerHost: PartnerHost,
  partnerName: string,
): Host => ({
  id: partnerHost.appUserId,
  displayName: partnerHost.displayName,
  email: partnerHost.email,
  createdAtUtc: "",
  updatedAt: "",
  accountState: PARTNER_STAFF_STATE,
  partnerName,
});

/** A host as the admin app names it: staff with their partner, "Pri R. · Acme". */
export const hostLabel = (host: Host): string =>
  host.partnerName
    ? `${host.displayName} · ${host.partnerName}`
    : host.displayName;
